import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { WebSerialPrinterService } from './webserial-printer.service';
import { WebUsbPrinterService } from './webusb-printer.service';

export interface GrantedPort {
  port: any;
  info: { usbVendorId?: number; usbProductId?: number; productName?: string };
  type?: 'serial' | 'usb';
}

@Injectable({ providedIn: 'root' })
export class PrinterDiscoveryService {
  private _printers$ = new BehaviorSubject<GrantedPort[]>([]);
  readonly printers$ = this._printers$.asObservable();

  private pollingId?: any;
  private lastDevices?: any[];
  private lastUsbDevices?: any[];

  constructor(
    private wsPrinter: WebSerialPrinterService,
    private usbPrinter: WebUsbPrinterService,
    private zone: NgZone
  ) {
    // Kick off initial load
    if (this.wsPrinter.isSupported || this.usbPrinter.isSupported) {
      this.refresh().catch(() => void 0);

      // Start polling for changes using NgZone to avoid triggering change detection constantly
      this.zone.runOutsideAngular(() => {
        // Serial polling
        if (this.wsPrinter.isSupported) {
          this.pollingId = setInterval(async () => {
            try {
              // @ts-ignore
              const currentPorts: any[] = await navigator.serial.getPorts();
              if (this.hasPortsChanged(currentPorts)) {
                this.lastDevices = currentPorts;
                await this.refresh();
              }
            } catch {
              // ignore polling errors
            }
          }, 2000);
        }

        // USB events
        if (this.usbPrinter.isSupported) {
          try {
            (navigator as any).usb?.addEventListener?.('connect', async () => {
              await this.refresh();
            });
            (navigator as any).usb?.addEventListener?.('disconnect', async () => {
              await this.refresh();
            });
          } catch {}

          // Also poll USB in case events aren't firing as expected
          setInterval(async () => {
            try {
              const currentUsb: any[] = await (navigator as any).usb.getDevices();
              if (this.hasUsbChanged(currentUsb)) {
                this.lastUsbDevices = currentUsb;
                await this.refresh();
              }
            } catch {}
          }, 3000);
        }
      });
    }
  }

  async refresh(): Promise<void> {
    try {
      const serials = this.wsPrinter.isSupported ? await this.wsPrinter.getGrantedPorts() : [];
      const usbs = this.usbPrinter.isSupported ? await this.usbPrinter.getGrantedDevices() : [];

      const all: GrantedPort[] = [
        ...serials.map(s => ({ ...s, type: 'serial' as const })),
        ...usbs.map(u => ({ port: u.device, info: u.info, type: 'usb' as const }))
      ];

      // Run inside Angular so subscribers update UI
      this.zone.run(() => this._printers$.next(all));
    } catch (e) {
      // Keep previous list on failure
    }
  }

  get snapshot(): GrantedPort[] {
    return this._printers$.getValue();
  }

  identifyGranted(p: GrantedPort): string {
    const adapted = { vendorId: p.info.usbVendorId, productId: p.info.usbProductId, productName: p.info.productName };
    const service = p.type === 'usb' ? this.usbPrinter : this.wsPrinter;
    return service.identifyPrinter(adapted as any);
  }

  setDefaultFromGranted(p: GrantedPort): void {
    const adapted = { vendorId: p.info.usbVendorId, productId: p.info.usbProductId };
    const transport = p.type || 'serial';
    localStorage.setItem('printerTransport', transport);

    const service = transport === 'usb' ? this.usbPrinter : this.wsPrinter;
    service.setDefaultPrinter(adapted as any);
  }

  private hasUsbChanged(currentDevices: any[]): boolean {
    if (!this.lastUsbDevices) {
      this.lastUsbDevices = currentDevices;
      return false;
    }
    if (currentDevices.length !== this.lastUsbDevices.length) return true;
    for (let i = 0; i < currentDevices.length; i++) {
      if (
        currentDevices[i].vendorId !== this.lastUsbDevices[i].vendorId ||
        currentDevices[i].productId !== this.lastUsbDevices[i].productId
      ) {
        return true;
      }
    }
    return false;
  }

  private hasPortsChanged(currentPorts: any[]): boolean {
    if (!this.lastDevices) {
      this.lastDevices = currentPorts;
      return false; // do not treat initial as change
    }

    if (currentPorts.length !== this.lastDevices.length) return true;

    for (let i = 0; i < currentPorts.length; i++) {
      const current = currentPorts[i];
      const previous = this.lastDevices[i];

      const currentInfo = typeof current.getInfo === 'function' ? current.getInfo() : {};
      const previousInfo = typeof previous.getInfo === 'function' ? previous.getInfo() : {};

      if (currentInfo.usbVendorId !== previousInfo.usbVendorId || currentInfo.usbProductId !== previousInfo.usbProductId) {
        return true;
      }
    }

    return false;
  }
}
